import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import maplibregl from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import { useLocation, useNavigate } from 'react-router-dom';

import DataPanel from '@App/components/DataPanel/DataPanel';
import { addMarkerImagesToMap } from '@App/components/Map/map.utils';
import { useAuth } from '@App/components/AuthProvider/auth-provider.context';

import SearchControl from './controls/SearchControl/SearchControl';
import DataPanelControl from './controls/DataPanelControl/DataPanelControl';
import KeyControl from './controls/KeyControl/KeyControl';
import ConsumerLegendControl from './controls/ConsumerLegendControl/ConsumerLegendControl.jsx';
import NavigationControl from './controls/NavigationControl/NavigationControl';

import 'maplibre-gl/dist/maplibre-gl.css';
import './map.css';

function Map({ map, setMap, outlet }) {
  const { isLoggedIn } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const dataPanelControlRef = useRef(null);
  const [dataPanelContainer, setDataPanelContainer] = useState(null);

  useEffect(() => {
    const protocol = new Protocol();
    maplibregl.addProtocol('pmtiles', protocol.tile);

    const maplibreMap = new maplibregl.Map({
      container: mapRef.current,
      attributionControl: {
        compact: false,
        customAttribution: '<a href="https://nazka.be/" target="_blank" rel="noopener noreferrer">Nazka Mapps</a>',
      },
      style: `https://api.maptiler.com/maps/019b31ff-0cf1-7e45-a555-91e6992c2e6a/style.json?key=${import.meta.env.VITE_MAPTILER_KEY}`,
      preserveDrawingBuffer: true,
      center: [4.2, 51.1],
      zoom: 8,
      maxZoom: 15.9,
      maxBounds: [
        [2.1016113281244486, 50.41019427107747],
        [6.29838867187425, 51.77966486336484],
      ],
    });

    maplibreMap.setMaxPitch(0);

    maplibreMap.addControl(new SearchControl(maplibreMap, navigate), 'top-left');
    maplibreMap.addControl(new NavigationControl(maplibreMap), 'top-right');

    maplibreMap.on('load', async () => {
      await addMarkerImagesToMap(maplibreMap);
    });

    maplibreMap.once('load', () => {
      setMap(maplibreMap);
    });

    return () => {
      maplibreMap.remove();
      maplibregl.removeProtocol('pmtiles');
    };
  }, [navigate, setMap]);

  useEffect(() => {
    if (!map) return undefined;

    const mapContainer = map.getContainer();
    const updateMapHeight = () => {
      mapContainer.style.setProperty('--map-height', `${mapContainer.clientHeight}px`);
    };
    const resizeObserver = new ResizeObserver(updateMapHeight);

    resizeObserver.observe(mapContainer);
    updateMapHeight();

    return () => {
      resizeObserver.disconnect();
      mapContainer.style.removeProperty('--map-height');
    };
  }, [map]);

  useEffect(() => {
    if (!map) return undefined;

    const dataPanelControl = new DataPanelControl();
    dataPanelControlRef.current = dataPanelControl;
    map.addControl(dataPanelControl, 'top-left');
    setDataPanelContainer(dataPanelControl.getContainer());

    return () => {
      map.removeControl(dataPanelControl);
      dataPanelControlRef.current = null;
      setDataPanelContainer(null);
    };
  }, [map]);

  useEffect(() => {
    const keyControl = new KeyControl(map);
    if (map && isLoggedIn) {
      map.addControl(keyControl, 'top-right');
    };
    return () => {
      if (map && keyControl) {
        map.removeControl(keyControl);
      }
    };
  }, [map, isLoggedIn]);

  useEffect(() => {
    if (!map || !pathname.startsWith('/afnemers')) return undefined;

    const legendControl = new ConsumerLegendControl();
    map.addControl(legendControl, 'bottom-right');

    return () => {
      map.removeControl(legendControl);
    };
  }, [map, pathname]);

  return (
    <>
      <div ref={mapRef} className="map" />
      {dataPanelContainer && createPortal(
        <DataPanel
          outlet={outlet}
          map={map}
          navigate={navigate}
          onContentChange={(isVisible) => {
            dataPanelContainer.hidden = !isVisible;
          }}
        />,
        dataPanelContainer,
      )}
    </>
  );
}

export default Map;
